import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  Input,
  Injector,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TuiDialogService } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG } from '@src/core';
import { AlertService } from '@src/core/services';
import { Multimedia } from '@src/models';
import { convertFileToMultimedia } from '@src/utils';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { BaseResizableComponent } from '@src/app/modules/base';
import { EnvService } from '@src/app/modules/env';

import { PhotoSize } from '../types';

@Component({
  selector: 'photo-edit',
  templateUrl: './photo-edit.component.html',
  styleUrls: ['./photo-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhotoEditComponent extends BaseResizableComponent {
  private readonly alertService = inject(AlertService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  readonly env = inject(EnvService);

  @Input() newPhoto?: Multimedia;
  @Input() photoSrc?: string;
  @Output() photoChange = new EventEmitter<Multimedia | undefined>();
  @Output() photoDelete = new EventEmitter<void>();

  @Input() size: PhotoSize = 'xxl';
  @Input() width?: string;
  @Input() height?: string;
  @Input() borderRadius?: string;
  @Input() rounded = false;
  @Input() loading = false;

  @ViewChild('photoFileInput') photoFileInput?: ElementRef;

  private readonly confirmPhotoDeleteDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: 'Удаление фото',
      size: 's',
      closeable: false,
    },
  );

  getPhotoUrl() {
    return this.newPhoto?.source?.src ?? this.photoSrc;
  }

  async onClickPhoto(e: any) {
    e.stopPropagation();

    if (!this.photoFileInput) {
      return;
    }

    if (!(await this.env.isAccessToCamera())) {
      return;
    }

    setTimeout(() => {
      if (this.photoFileInput) {
        this.photoFileInput.nativeElement.value = '';
        this.photoFileInput.nativeElement.click();
      }
    }, 500);
  }

  photoFileInputChange(event: Event): void {
    const files: FileList | null = (event.target as HTMLInputElement).files;
    if (!files?.length || !this.photoFileInput) return;

    const file = files.item(0);
    if (!file) return;

    if (file.size > APP_CONFIG.fileSizeMax.image) {
      this.alertService.error(this.translateService.instant('common.alerts.errors.fileSizeMax'));

      this.photoFileInput.nativeElement.value = '';
      this.newPhoto = undefined;
    } else {
      this.newPhoto = convertFileToMultimedia(file);
    }

    this.photoChange.emit(this.newPhoto);

    this.cdr.markForCheck();
  }

  onPhotoDelete(): void {
    this.newPhoto = undefined;

    if (this.photoSrc) {
      this.confirmPhotoDeleteDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
        next: res => {
          if (res === true) {
            this.photoDelete.emit();
          }
        },
      });
    } else {
      this.photoChange.emit();
    }

    this.cdr.markForCheck();
  }
}
